// Pure value extractor — handles flat keys and source/yearIndex projection paths
export const getMetricValue = (result, metricKey, source, yearIndex) => {
    if (!result) return 0;
    if (source && yearIndex !== undefined) {
        const arr = result[source];
        return arr && arr[yearIndex] ? arr[yearIndex][metricKey] || 0 : 0;
    }
    return result[metricKey] ?? 0;
};

// Returns indices of all winning columns (multiple if tied), or [] if no meaningful winner.
// If every column has the identical value, returns [] — no comparison signal exists.
export const getBestIndices = (values, highlight) => {
    if (!highlight || values.length < 2) return [];
    const nums = values.map(v => typeof v === 'number' ? v : 0);
    const target = highlight === 'max' ? Math.max(...nums) : highlight === 'min' ? Math.min(...nums) : null;
    if (target === null) return [];
    const indices = [];
    nums.forEach((v, i) => { if (v === target) indices.push(i); });
    if (indices.length === values.length) return [];
    return indices;
};

// Single source of truth for which rows compete and how.
// Must stay in sync with the <MetricRow highlight=...> calls in ComparisonTable.js.
export const HIGHLIGHTED_METRICS = [
    { metricKey: 'cashOnCashROI', highlight: 'max' },
    { metricKey: 'monthlyCashFlow', highlight: 'max' },
    { metricKey: 'monthlyMortgage', highlight: 'min' },
    { metricKey: 'netOperatingIncome', highlight: 'max' },
    { metricKey: 'capRate', highlight: 'max' },
    { metricKey: 'totalROI', highlight: 'max' },
    { metricKey: 'grossRentalIncome', highlight: 'max' },
    { metricKey: 'totalOperatingExpenses', highlight: 'min' },
    { metricKey: 'cashInvested', highlight: 'min' },
    { metricKey: 'equity', source: 'equityData', yearIndex: 4, highlight: 'max' },
    { metricKey: 'equity', source: 'equityData', yearIndex: 9, highlight: 'max' },
    { metricKey: 'cashFlow', source: 'cashFlowProjection', yearIndex: 4, highlight: 'max' },
    { metricKey: 'cashFlow', source: 'cashFlowProjection', yearIndex: 9, highlight: 'max' },
];

// Returns { columnId: winCount } map.
export const computeWinsByColumn = (results, columnIds) => {
    const wins = Object.fromEntries(columnIds.map(id => [id, 0]));
    if (columnIds.length < 2) return wins;
    HIGHLIGHTED_METRICS.forEach(def => {
        const values = columnIds.map(id => getMetricValue(results[id], def.metricKey, def.source, def.yearIndex));
        const bestIndices = getBestIndices(values, def.highlight);
        bestIndices.forEach(i => { wins[columnIds[i]] += 1; });
    });
    return wins;
};
